/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from "react";

import {
  CheckboxVisibility,
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  Stack,
  Text,
} from "@fluentui/react";

import {
  headerStackStyles,
  headerTextStyles,
  ContainerWrapper,
  DetailsListWrapper,
  TableTextWrapper,
} from "Components/Common/SharedTableStyles.styles";
import CopyButton from "Components/CopyButton/CopyButton";
import { BenefitDisplayNameEnum } from "Constants/BenefitConstants";
import { IM365CopilotBenefitDetail } from "Interfaces/Benefits/IM365CopilotBenefitDetail";

import { IM365CopilotBenefitDetailProps } from "./IM365CopilotBenefitDetailProps";
import { copyIconStyles } from "./M365CopilotBenefitDetail.styles";

const M365CopilotBenefitDetail: React.FunctionComponent<IM365CopilotBenefitDetailProps> = (
  props: IM365CopilotBenefitDetailProps
) => {
  const { benefits } = props;

  const columns = [
    {
      key: "copilotTenantId",
      name: "Tenant ID",
      fieldName: "copilotTenantId",
      minWidth: 200,
      maxWidth: 300,
      isResizable: true,
    },
    {
      key: "status",
      name: "Status",
      fieldName: "status",
      minWidth: 100,
      maxWidth: 200,
      isResizable: true,
    },
    {
      key: "claimedDate",
      name: "Claimed Date",
      fieldName: "claimedDate",
      minWidth: 100,
      maxWidth: 200,
      isResizable: true,
    },
    {
      key: "awardPeriod",
      name: "Award Period",
      fieldName: "awardPeriod",
      minWidth: 100,
      maxWidth: 250,
      isResizable: true,
    },
  ];

  const renderItemColumn = (
    item: IM365CopilotBenefitDetail,
    index?: number,
    column?: IColumn
  ): JSX.Element => {
    switch (column?.key) {
      //add a copy button to the registration id value to clipboard
      case "copilotTenantId":
        return (
          <Stack horizontal verticalAlign="center">
            <span>{item.copilotTenantId}</span>
            <CopyButton styles={copyIconStyles} text={item.copilotTenantId} showCopyText={true} />
          </Stack>
        );
      case "claimedDate":
        if (item.claimedDate === null) {
          return <span></span>;
        }

        const claimedDate = new Date(item.claimedDate).toDateString();
        return <span>{claimedDate}</span>;
      default:
        const fieldContent = item[column?.fieldName as keyof IM365CopilotBenefitDetail] as string;
        return <span>{fieldContent}</span>;
    }
  };

  return (
    <ContainerWrapper>
      <Stack styles={headerStackStyles}>
        <Text styles={headerTextStyles}>{BenefitDisplayNameEnum.M365Copilot}</Text>
      </Stack>
      <DetailsListWrapper>
        <DetailsList
          items={benefits}
          columns={columns}
          setKey="set"
          layoutMode={DetailsListLayoutMode.justified}
          ariaLabelForSelectionColumn="Toggle selection"
          ariaLabelForSelectAllCheckbox="Toggle selection for all items"
          checkButtonAriaLabel="select row"
          checkboxVisibility={CheckboxVisibility.hidden}
          onRenderItemColumn={renderItemColumn}
        />
      </DetailsListWrapper>
      {benefits && benefits.length < 1 && (
        <TableTextWrapper>No {BenefitDisplayNameEnum.M365Copilot} for this user.</TableTextWrapper>
      )}
    </ContainerWrapper>
  );
};

export default M365CopilotBenefitDetail;
